import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { apiGet } from '@/lib/http/client';
import { portfolioQueryKeys, portfoliosUrl } from '@/lib/portfolio/api';
import type { Portfolio } from '@/lib/portfolio/types';

import { useUser } from '../allauth_auth/hooks.js';

/**
 * Shared React Query hook — the descriptor source of truth for the current
 * user's portfolios. Treats a 403/404 ApiError as "no access" (expected,
 * resolved to []) but surfaces every other failure — 5xx, network, timeout —
 * as a query error so callers can distinguish an outage from no access.
 */
export function usePortfolios() {
  const userId = useUser()?.id;

  return useQuery({
    queryKey: portfolioQueryKeys.list(userId),
    queryFn: async (): Promise<Portfolio[]> => {
      try {
        return await apiGet<Portfolio[]>(portfoliosUrl());
      } catch (e) {
        if (
          e instanceof ApiError &&
          (e.statusCode === 403 || e.statusCode === 404)
        ) {
          return [];
        }
        throw e;
      }
    },
    enabled: userId !== undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
}

/**
 * Tri-state gate derived from usePortfolios. For a hard gate (route guards)
 * that must distinguish an outage from confirmed no-access, consume
 * usePortfolios() directly instead — see PortfolioGuard.
 * - null: still loading, OR the query errored (data is unset) — a nav item
 *   keyed off this should stay hidden during an outage, not claim "no access"
 * - true: user has at least one portfolio
 * - false: user has no portfolios (resolved, no access)
 */
export function usePortfolioAccess(): boolean | null {
  const { data } = usePortfolios();
  return data === undefined ? null : data.length > 0;
}
